import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, map, of, throwError } from 'rxjs';
import {
  LeaderboardTriviaPort,
  type LoadTodayTriviaParams,
  type SubmitTriviaAnswerParams,
} from '../../../domain/leaderboard/ports/leaderboard-trivia.port';
import type {
  LeaderboardTriviaAnswerResult,
  LeaderboardTriviaToday,
} from '../../../domain/leaderboard/models/leaderboard-trivia.model';
import type {
  LeaderboardEventsDataResponse,
  TriviaAnswerApiBody,
  TriviaAnswerApiData,
  TriviaTodayApiData,
} from '../dtos/leaderboard-trivia-api.dto';
import { mapTriviaAnswerApiToDomain, mapTriviaTodayApiToDomain } from '../mappers/map-trivia-api-to-domain';
import { ENVIRONMENT } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class LeaderboardTriviaHttpAdapter extends LeaderboardTriviaPort {
  constructor(private readonly http: HttpClient) {
    super();
  }

  loadToday(params: LoadTodayTriviaParams): Observable<LeaderboardTriviaToday | null> {
    const url = this.todayUrl(params);
    if (!url) {
      return of(null);
    }

    return this.http
      .get<LeaderboardEventsDataResponse<TriviaTodayApiData>>(url)
      .pipe(
        map((res) => mapTriviaTodayApiToDomain(res?.data)),
        catchError(() => of(null)),
      );
  }

  submitAnswer(params: SubmitTriviaAnswerParams): Observable<LeaderboardTriviaAnswerResult | null> {
    const url = this.answerUrl(params);
    if (!url) {
      return of(null);
    }

    const body: TriviaAnswerApiBody = {
      uid: params.uid.trim(),
      day: params.day,
      answer: params.answer,
    };

    return this.http
      .post<LeaderboardEventsDataResponse<TriviaAnswerApiData>>(url, body)
      .pipe(
        map((res) => mapTriviaAnswerApiToDomain(res?.data)),
        catchError((err) => throwError(() => err)),
      );
  }

  private apiPrefix(): string | null {
    const base = ENVIRONMENT.leaderboardEventsApiUrl?.trim();
    if (!base) {
      return null;
    }
    return `${base.replace(/\/$/, '')}/commerce`;
  }

  private todayUrl(params: LoadTodayTriviaParams): string | null {
    const prefix = this.apiPrefix();
    const lb = params.leaderboardId?.trim();
    const uid = encodeURIComponent(params.uid.trim());
    if (!prefix || !lb || !params.uid?.trim()) {
      return null;
    }
    return `${prefix}/${params.commerceId}/leaderboard/${encodeURIComponent(lb)}/trivia/today?uid=${uid}`;
  }

  private answerUrl(params: SubmitTriviaAnswerParams): string | null {
    const prefix = this.apiPrefix();
    const lb = params.leaderboardId?.trim();
    if (!prefix || !lb || !params.uid?.trim()) {
      return null;
    }
    return `${prefix}/${params.commerceId}/leaderboard/${encodeURIComponent(lb)}/trivia/answer`;
  }
}
